import {
  Box,
  BoxProps,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { CSSProperties, useMemo } from 'react';

import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';

export enum BadgeVariants {
  Primary = 'primary',
  Accent = 'accent',
  Warning = 'warning',
  SecondaryFilled = 'secondary',
  Success = 'success',
  SecondaryOutline = 'secondary-outline',
  Destructive = 'destructive',
}

const STATIC_BADGE_VARIANT_STYLES: Record<
  Exclude<BadgeVariants, BadgeVariants.Primary>,
  SxProps<Theme>
> = {
  [BadgeVariants.Accent]: {
    backgroundColor: COLORS.BLUE[100],
    outline: `1px solid ${COLORS.BLUE[300]}`,
    color: COLORS.BLUE[900],
  },
  [BadgeVariants.Warning]: {
    backgroundColor: COLORS.FUNCTIONAL.WARNING[100],
    outline: `1px solid ${COLORS.FUNCTIONAL.WARNING[300]}`,
    color: COLORS.FUNCTIONAL.WARNING[900],
  },
  [BadgeVariants.SecondaryFilled]: {
    backgroundColor: COLORS.GRAY[200],
    outline: `1px solid ${COLORS.GRAY[200]}`,
    color: COLORS.TEXT.PRIMARY,
  },
  [BadgeVariants.SecondaryOutline]: {
    backgroundColor: COLORS.PRIMITIVES.WHITE,
    outline: `1px solid ${COLORS.GRAY[200]}`,
    color: COLORS.TEXT.PRIMARY,
  },
  [BadgeVariants.Success]: {
    backgroundColor: COLORS.FUNCTIONAL.SUCCESS[100],
    outline: `1px solid ${COLORS.FUNCTIONAL.SUCCESS[300]}`,
    color: COLORS.FUNCTIONAL.SUCCESS[900],
  },
  [BadgeVariants.Destructive]: {
    backgroundColor: COLORS.FUNCTIONAL.ERROR[100],
    outline: `1px solid ${COLORS.FUNCTIONAL.ERROR[300]}`,
    color: COLORS.FUNCTIONAL.ERROR[900],
  },
};

export const getBadgeStyles = (
  theme: Theme
): Record<BadgeVariants, SxProps<Theme>> => ({
  [BadgeVariants.Primary]: {
    backgroundColor: theme.palette.primary.main,
    outline: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.contrastText,
  },
  ...STATIC_BADGE_VARIANT_STYLES,
});

export interface BadgeProps extends Omit<BoxProps, 'display'> {
  variant: BadgeVariants;
  display: React.ReactNode;
  textTransform?: CSSProperties['textTransform'];
  // Optional styling for the Typography component of the badge
  sx?: SxProps<Theme>;
  // Optional styling for the Box component that wraps the Typography component
  wrapperSx?: SxProps<Theme>;
}

export function Badge({
  variant,
  display,
  textTransform = 'none',
  sx = {},
  wrapperSx,
  ...wrapperProps
}: BadgeProps) {
  const theme = useTheme();
  const badgeStyles = getBadgeStyles(theme);

  const style = useMemo(() => {
    return badgeStyles[variant];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- badgeStyles is stable for a given theme
  }, [variant, theme]);

  const sxProps: SxProps<Theme> = {
    ...style,
    py: 0,
    px: '6px',
    borderRadius: '4px',
    width: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    textTransform: textTransform,
    lineHeight: '16px',
  };

  Object.assign(sxProps ? sxProps : {}, sx);

  return (
    <Box sx={wrapperSx} {...wrapperProps}>
      <Typography
        variant="title-xs"
        fontWeight={FONT_WEIGHTS.medium}
        component="span"
        sx={sxProps}
      >
        {display}
      </Typography>
    </Box>
  );
}
